import { useState, useEffect, useCallback, useRef } from 'react';
import { FormInstance } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import { useIsOnlineContext } from '../contexts/IsOnlineContext';
import { useNotificationContext } from '../contexts/NotificationContext';
import { productRepository } from '../api/repositories/productRepository';
import { localDataService } from '../services/localDataService';
import { useLocalData } from './useLocalData';
import { PresentationStatusEnum } from '../enums/PresentationStatusEnum';
import { trimValues } from '../utils/trimValues';
import dayjs from 'dayjs';
import RESOURCE_CONFIGS from '../constants/resourceConfig';
import { convertDayjsToEpoch } from '../utils/convertDayjsToEpoch';
import { referenceDataSyncService } from '../services/referenceDataSyncService';

interface UsePresentationFormOptionsProps {
  form: FormInstance;
  triggeredFromTask?: boolean;
  productFromTask?: any;
  presentationFromTask?: any;
  status: PresentationStatusEnum;
  resetProductSection?: number;
  resetPresentationSection?: number;
  selectedCategories?: number[];
  setSelectedCategories?: React.Dispatch<React.SetStateAction<number[]>>;
  addProductForm?: FormInstance;
  addPresentationForm?: FormInstance;
  onSuccess?: (enrichedProduct: any) => void;
  onShowSelectedSectionChange?: (visible: boolean) => void;
}

export function usePresentationFormOptions({
  form,
  triggeredFromTask = false,
  productFromTask,
  presentationFromTask,
  status,
  resetProductSection = 0,
  resetPresentationSection = 0,
  selectedCategories = [],
  setSelectedCategories,
  addProductForm,
  addPresentationForm,
  onSuccess,
  onShowSelectedSectionChange,
}: UsePresentationFormOptionsProps) {
  const { t } = useTranslation(['price', 'form', 'product']);
  const { isOnline } = useIsOnlineContext();
  const { successNotification, errorNotification } = useNotificationContext();

  const taskProd = presentationFromTask || productFromTask;
  const resetSec = resetPresentationSection !== 0 ? resetPresentationSection : resetProductSection;
  const addForm = addPresentationForm || addProductForm;

  const { create: createNewProduct, updateLocalData } = useLocalData({
    entity: 'presentation',
    autoSync: true,
  });

  // Local state
  const [enrichedProducts, setEnrichedProducts] = useState<any[]>([]);
  const [enrichedProductsOptions, setEnrichedProductsOptions] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any[]>([]);
  const [showSelectedProductSection, setShowSelectedProductSection] = useState<boolean>(false);
  const [allMainProducts, setAllMainProducts] = useState<any[]>([]);
  const [mainProductOptions, setMainProductOptions] = useState<any[]>([]);
  const [isProductModalOpen, setIsProductModalOpen] = useState<boolean>(false);
  const [autoSelectedNewProduct, setAutoSelectedNewProduct] = useState<string | undefined>(
    undefined
  );
  const [isScannerModalOpen, setIsScannerModalOpen] = useState(false);
  const [hasCustomFields, setHasCustomFields] = useState<boolean>(false);
  const [loadingMainProducts, setLoadingMainProducts] = useState<boolean>(true);
  const [loadingPresentations, setLoadingPresentations] = useState<boolean>(true);

  const selectedMainProductId = useWatch('mainProductName', form);
  const scanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (onShowSelectedSectionChange) {
      onShowSelectedSectionChange(showSelectedProductSection);
    }
  }, [showSelectedProductSection, onShowSelectedSectionChange]);

  // Load products and main products from IndexedDB only, and periodically check if online and empty
  useEffect(() => {
    let active = true;
    let intervalId: any = null;

    const loadData = async () => {
      if (!active) return;

      setLoadingMainProducts(true);
      try {
        await productRepository.loadMainProducts(
          false, // IndexedDB only
          (response) => {
            if (!active) return;
            const mappedMains = response.map((p: any) => ({
              label: p?.name,
              value: p?.id,
              id: p?.id,
              unit: p?.unit,
              productCategoryId: p?.productCategory?.id,
              productCategory: p?.productCategory,
              presentations: p?.presentations,
              brands: p?.brands,
            }));
            setAllMainProducts(mappedMains);
            if (isOnline && mappedMains.length === 0) {
              setLoadingMainProducts(true);
            } else {
              setLoadingMainProducts(false);
            }
          },
          () => {
            // Completed
          }
        );
      } catch (err) {
        if (active) setLoadingMainProducts(false);
      }

      setLoadingPresentations(true);
      try {
        await productRepository.loadPresentations(
          false, // IndexedDB only
          (allProducts) => {
            if (!active) return;
            const uniqueProducts = allProducts.filter(
              (product, index, self) => index === self.findIndex((p) => p.refId === product.refId)
            );
            setEnrichedProducts(uniqueProducts);
            if (isOnline && uniqueProducts.length === 0) {
              setLoadingPresentations(true);
            } else {
              setLoadingPresentations(false);
            }
          },
          () => {
            // Completed
          }
        );
      } catch (error) {
        if (active) setLoadingPresentations(false);
      }
    };

    loadData();

    if (isOnline) {
      intervalId = setInterval(async () => {
        if (!active) return;
        const localMainProducts = await productRepository.loadMainProducts(false);
        const localPresentations = await productRepository.loadPresentations(false);
        if (localMainProducts.length > 0 && localPresentations.length > 0) {
          await loadData();
          clearInterval(intervalId);
        }
      }, 2000);
    }

    return () => {
      active = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isOnline]);

  // Filter mainProductOptions based on selectedCategories
  useEffect(() => {
    const filtered = productRepository.filterMainProductsByCategory(
      allMainProducts,
      selectedCategories || []
    );
    setMainProductOptions(filtered);
  }, [selectedCategories, allMainProducts]);

  // Populate presentation finder options based on selectedMainProductId
  useEffect(() => {
    let active = true;
    const fetchPresentations = async () => {
      if (selectedMainProductId) {
        setLoadingPresentations(true);
        try {
          await productRepository.loadPresentationsByMainProduct(
            false, // Retrieve from IndexedDB only
            selectedMainProductId,
            (filtered) => {
              if (!active) return;
              setEnrichedProducts((prev) => {
                const merged = [...prev, ...filtered];
                return merged.filter(
                  (p, index, self) => index === self.findIndex((item) => item.refId === p.refId)
                );
              });

              const selectedMainProd = mainProductOptions.find(
                (p: any) => String(p.id) === String(selectedMainProductId)
              );

              const options = filtered.map((product: any) => ({
                key: product?.refId,
                value: product?.name || product?.productName,
                labelText: product?.name || product?.productName,
                subValues: [
                  product?.category || selectedMainProd?.productCategory?.mainCategory,
                  product?.unit,
                  product?.brand,
                ].filter(Boolean),
              }));
              setEnrichedProductsOptions(options);
            },
            () => {
              if (active) {
                setLoadingPresentations(false);
              }
            }
          );
        } catch (error) {
          if (active) {
            setEnrichedProductsOptions([]);
            setLoadingPresentations(false);
          }
        }
      } else {
        setEnrichedProductsOptions([]);
        setLoadingPresentations(false);
      }
    };

    fetchPresentations();

    return () => {
      active = false;
    };
  }, [selectedMainProductId, isOnline, mainProductOptions]);

  // Reset selection when selectedMainProductId changes and doesn't match
  useEffect(() => {
    if (selectedProduct && selectedProduct.length > 0) {
      const prod = selectedProduct[0];
      const mainId = prod.mainProductId || prod.mainProduct?.id || prod.mainProduct;
      if (String(mainId) !== String(selectedMainProductId)) {
        form.setFieldValue('productSearch', undefined);
        setSelectedProduct([]);
        setShowSelectedProductSection(false);
        form.setFieldsValue({
          productName: undefined,
          productRefId: undefined,
          category: undefined,
          subCategory: undefined,
        });
      }
    }
  }, [selectedMainProductId]);

  // Barcode scanning lookup
  const lookupBarcode = async (barcode: string): Promise<any> => {
    try {
      const dbProduct = await localDataService.getProductByBarcode(barcode);
      if (dbProduct) {
        const mainId =
          dbProduct.mainProductId || dbProduct.mainProduct?.id || dbProduct.mainProduct;
        const mainProd = allMainProducts.find((p: any) => String(p.id) === String(mainId));
        return {
          ...dbProduct,
          productCategory: mainProd?.productCategory,
        };
      }
    } catch (e) {
      console.warn('DB lookup failed', e);
    }

    const memoryProduct = enrichedProducts?.find((p: any) => p.barcode === barcode);
    if (memoryProduct) {
      const mainId =
        memoryProduct.mainProductId || memoryProduct.mainProduct?.id || memoryProduct.mainProduct;
      const mainProd = allMainProducts.find((p: any) => String(p.id) === String(mainId));
      return {
        ...memoryProduct,
        productCategory: mainProd?.productCategory,
      };
    }

    return null;
  };

  // Handle barcode scan success
  const onScanSuccess = (decodedText: string) => {
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }

    scanTimeoutRef.current = setTimeout(async () => {
      if (!isScannerModalOpen) return;
      setIsScannerModalOpen(false);
      console.info('Scanned barcode: ', decodedText);

      const foundProduct = await lookupBarcode(decodedText);

      if (foundProduct) {
        successNotification(`Product found: ${foundProduct.name}`);

        const mainId =
          foundProduct.mainProductId || foundProduct.mainProduct?.id || foundProduct.mainProduct;
        if (mainId) {
          form.setFieldValue('mainProductName', mainId);
        }
        form.setFieldsValue({
          productSearch: foundProduct.name,
          productName: foundProduct.name,
          productRefId: foundProduct.refId,
        });

        if (foundProduct.productCategoryId) {
          if (setSelectedCategories) {
            setSelectedCategories([foundProduct.productCategoryId]);
          }
        }

        if (foundProduct.productCategory) {
          form.setFieldValue('category', foundProduct.productCategory.mainCategory);
          form.setFieldValue('subCategory', foundProduct.productCategory.category);
        }

        // Add to enriched list so it shows up in future searches/dropdowns
        setEnrichedProducts((prev) => {
          const exists = prev.some((p: any) => p.refId === foundProduct.refId);
          return exists ? prev : [...prev, foundProduct];
        });

        setSelectedProduct([foundProduct]);
        setShowSelectedProductSection(true);
        setAutoSelectedNewProduct(foundProduct.name);
      } else {
        errorNotification(t('Product not found with this barcode'));
      }
    }, 500);
  };

  const handlescanFailure = (error: any) => {
    // console.warn(error);
  };

  // Search filter handler for finder dropdown
  const onSearch = useCallback(
    (val: any) => {
      if (val && !triggeredFromTask) {
        const filteredProducts = enrichedProducts?.filter((product) => {
          const nameStr = (product?.name || product?.productName || '').toString().toLowerCase();
          const valStr = val.toString().toLowerCase();
          return nameStr === valStr || nameStr.includes(valStr);
        });

        if (filteredProducts && filteredProducts.length > 0) {
          const selectedProd: any = filteredProducts[0];

          const selectedMainProd = mainProductOptions.find(
            (p: any) => String(p.id) === String(selectedMainProductId)
          );

          form.setFieldsValue({
            productName: selectedProd.name || selectedProd.productName,
            productRefId: selectedProd.refId,
            category: selectedProd?.category || selectedMainProd?.productCategory?.mainCategory,
            subCategory: selectedProd?.subCategory || selectedMainProd?.productCategory?.category,
          });

          setSelectedProduct(filteredProducts);
          setShowSelectedProductSection(true);
        } else {
          setSelectedProduct([]);
          setShowSelectedProductSection(false);
          form.setFieldsValue({
            productName: undefined,
            productRefId: undefined,
            category: undefined,
            subCategory: undefined,
          });
        }
      } else if (val && triggeredFromTask) {
        form.setFieldsValue({
          productName: taskProd?.name,
          productRefId: taskProd?.refId,
        });

        setSelectedProduct(taskProd);
        setShowSelectedProductSection(true);
      } else {
        setSelectedProduct([]);
        setShowSelectedProductSection(false);
        form.resetFields();
      }
    },
    [triggeredFromTask, enrichedProducts, form, taskProd, selectedMainProductId, mainProductOptions]
  );

  // Fetch custom fields for selectedProduct
  useEffect(() => {
    const fetchCustomFields = async () => {
      setHasCustomFields(false);

      if (
        selectedProduct[0]?.customFields &&
        selectedProduct[0]?.productCategory &&
        selectedProduct[0]?.productCategory.customFields?.fields
      ) {
        const customFieldsFromCategory = selectedProduct[0].productCategory.customFields?.fields;

        const addMetaData = (customFieldData: any, metadata: any[]) => {
          const customFields = metadata.map((field) => {
            const raw = customFieldData[field.key];

            return {
              fieldName: field.fieldName ?? null,
              value:
                field.dataType === 'date'
                  ? raw
                    ? dayjs(raw).format(RESOURCE_CONFIGS.DATE_TIME.DEFAULT_FORMAT)
                    : null
                  : (raw ?? null),
            };
          });

          return customFields;
        };

        const formCustomFields = addMetaData(
          selectedProduct[0].customFields,
          customFieldsFromCategory
        );

        selectedProduct[0].customFieldsMetadata = formCustomFields;
        setHasCustomFields(true);
      }
    };

    if (!triggeredFromTask && !taskProd) {
      fetchCustomFields();
    }
  }, [selectedProduct]);

  // Fetch custom fields for taskProd (triggered from task)
  useEffect(() => {
    const fetchCustomFields = async () => {
      if (taskProd?.customFields && taskProd?.productCategoryId) {
        const categoriesData: any = await referenceDataSyncService.getProductCategoryById(
          taskProd.productCategoryId
        );
        const addMetaData = (customFieldData: any, metadata: any[]) => {
          const customFields = metadata.map((field) => {
            const raw = customFieldData[field.key];

            return {
              fieldName: field.fieldName ?? null,
              value:
                field.dataType === 'date'
                  ? raw
                    ? dayjs(raw).format(RESOURCE_CONFIGS.DATE_TIME.DEFAULT_FORMAT)
                    : null
                  : (raw ?? null),
            };
          });

          return customFields;
        };
        const formCustomFields = addMetaData(
          taskProd?.customFields,
          categoriesData?.customFields?.fields
        );
        taskProd.customFieldsMetadata = formCustomFields;
        setHasCustomFields(true);
      }
    };
    setHasCustomFields(false);
    if (triggeredFromTask && taskProd) fetchCustomFields();
  }, [triggeredFromTask, taskProd]);

  // Auto select product when triggered from task changes
  useEffect(() => {
    if (triggeredFromTask && taskProd) {
      setAutoSelectedNewProduct(taskProd?.name);
      setShowSelectedProductSection(true);
      onSearch(taskProd?.name);
    }
  }, [triggeredFromTask, taskProd, onSearch]);

  // Reset product selection state
  useEffect(() => {
    setShowSelectedProductSection(false);
    setSelectedProduct([]);
  }, [resetSec]);

  // Auto-populate product name in modal form if pre-selected in parent
  useEffect(() => {
    if (isProductModalOpen && selectedMainProductId && addForm) {
      const selectedMainProd = allMainProducts.find(
        (p: any) => String(p.id) === String(selectedMainProductId)
      );
      if (selectedMainProd) {
        const timer = setTimeout(() => {
          addForm.setFieldsValue({
            mainProductName: selectedMainProductId,
          });
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [isProductModalOpen, selectedMainProductId, allMainProducts, addForm]);

  const handleNewProductButton = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (selectedCategories && selectedCategories.length > 0) {
      setSelectedCategories?.([]);
    }
    setIsProductModalOpen(true);
  };

  const closeNewProductModal = () => {
    addForm?.resetFields();
    setIsProductModalOpen(false);
  };

  const handleCancel = () => {
    addForm?.resetFields();
    setIsProductModalOpen(false);
  };

  const handleNewProductAddition = async () => {
    if (!addForm) return;

    const newProductFormData = addForm.getFieldsValue();

    const trimmedValues = trimValues(newProductFormData);
    addForm.setFieldsValue(trimmedValues);
    await addForm.validateFields();

    // Extract IDs or Custom Strings
    const formMainProductId = Number(trimmedValues.mainProductName); // holds ID

    const isCustomBrand = trimmedValues.brand === 'Other';
    const formBrandId = isCustomBrand ? null : Number(trimmedValues.brand);
    const formCustomBrandStr = isCustomBrand ? (trimmedValues.customBrand || '').trim() : '';

    const isCustomPresentation = trimmedValues.presentation === 'Other';
    const formPresentationId = isCustomPresentation ? null : Number(trimmedValues.presentation);
    const formCustomPresentationStr = isCustomPresentation
      ? (trimmedValues.customPresentation || '').trim()
      : '';

    // Check duplicate presentations (same product, brand, and presentation) using centralized utility
    const allPresentations = await productRepository.loadPresentations(false);

    const isDuplicate = productRepository.checkDuplicatePresentation(allPresentations, {
      mainProductId: formMainProductId,
      brandId: formBrandId,
      brandStr: formCustomBrandStr,
      presentationId: formPresentationId,
      presentationStr: formCustomPresentationStr,
    });

    if (isDuplicate) {
      errorNotification(t('product:duplicatePresentationError'));
      return;
    }

    // Check barcode duplicate using centralized utility
    const isBarcodeDuplicate = productRepository.checkBarcodeDuplicate(
      allPresentations,
      trimmedValues.barcode
    );

    if (isBarcodeDuplicate) {
      errorNotification(t('product:barcodeAlreadyExists'));
      return;
    }

    const convertedCustomFields = convertDayjsToEpoch(trimmedValues.customFields);

    const body = {
      name: trimmedValues?.name,
      // Fallback to label if it's an existing ID option
      brand: isCustomBrand ? formCustomBrandStr : trimmedValues?.brandLabel,
      presentation: isCustomPresentation
        ? formCustomPresentationStr
        : trimmedValues?.presentationLabel,
      barcode: trimmedValues?.barcode?.trim() ? trimmedValues.barcode.trim() : null,
      isActive: true,
      customFields: convertedCustomFields ? convertedCustomFields : null,
      equivalentAmount:
        trimmedValues?.equivalentAmount !== undefined &&
        trimmedValues.equivalentAmount !== null &&
        trimmedValues.equivalentAmount !== ''
          ? Number(trimmedValues.equivalentAmount)
          : null,
      mainProductName: trimmedValues?.mainProductLabel,
      mainProductId: Number(trimmedValues?.mainProductName),
      unit: trimmedValues?.unit,
    };

    const syncPayload = {
      name: body.name,
      brand: isCustomBrand ? formCustomBrandStr : null,
      brandId: formBrandId,
      presentation: isCustomPresentation ? formCustomPresentationStr : null,
      presentationId: formPresentationId,
      barcode: body.barcode,
      customFields: body.customFields,
      equivalentAmount: body.equivalentAmount,
      unit: body.unit,
      mainProductId: body.mainProductId,
    };

    try {
      const result = await createNewProduct(body, syncPayload);

      if (result) {
        const newProductData = {
          ...result,
          ...body,
          priceCount: 0,
          createTime: Date.now(),
          updateTime: Date.now(),
          synced: 0,
        };

        const selectedMainProd = mainProductOptions.find(
          (p: any) => String(p.id) === String(newProductData.mainProductId)
        );

        const enrichedProduct = {
          ...newProductData,
          category: selectedMainProd?.productCategory?.mainCategory || null,
          subCategory: selectedMainProd?.productCategory?.category || null,
          productCategory: selectedMainProd?.productCategory || null,
          productCategoryId: selectedMainProd?.productCategory?.id || null,
        };

        setEnrichedProducts((prevProducts) => [enrichedProduct, ...prevProducts]);
        updateLocalData((prevProducts) => [newProductData, ...prevProducts]);

        addForm.resetFields();
        successNotification(t('product:productCreateSuccess'));
        setSelectedProduct([enrichedProduct]);

        const mainId = enrichedProduct.mainProductId;
        form.setFieldsValue({
          mainProductName: mainId,
          productSearch: enrichedProduct.name,
          productName: enrichedProduct.name,
          productRefId: enrichedProduct.refId,
          category: enrichedProduct.category,
          subCategory: enrichedProduct.subCategory,
        });
        setAutoSelectedNewProduct(enrichedProduct.name);

        if (enrichedProduct.productCategoryId && setSelectedCategories) {
          const categoriesData = await referenceDataSyncService.getProductCategories();
          const mainCategory = selectedMainProd?.productCategory?.mainCategory;
          const mainCategoryIds = categoriesData
            ?.filter((cat: any) => cat.mainCategory === mainCategory)
            ?.map((cat: any) => cat.id) || [enrichedProduct.productCategoryId];

          setSelectedCategories(mainCategoryIds);
        }

        setShowSelectedProductSection(true);
        setIsProductModalOpen(false);

        if (onSuccess) {
          onSuccess(enrichedProduct);
        }
      }
    } catch (error) {
      console.error('Failed to create new product/presentation:', error);
      errorNotification(t('product:productCreateFail'));
    }
  };

  return {
    isOnline,
    allMainProducts,
    mainProductOptions,
    enrichedProducts,
    enrichedProductsOptions,
    selectedProduct,
    setSelectedProduct,
    showSelectedProductSection,
    setShowSelectedProductSection,
    isProductModalOpen,
    setIsProductModalOpen,
    autoSelectedNewProduct,
    setAutoSelectedNewProduct,
    isScannerModalOpen,
    setIsScannerModalOpen,
    hasCustomFields,
    onScanSuccess,
    handlescanFailure,
    onSearch,
    handleNewProductButton,
    closeNewProductModal,
    handleCancel,
    handleNewProductAddition,
    loadingMainProducts,
    loadingPresentations,
  };
}
